// ============================================
// Chat Messaging - Formatter (ES6)
// 메시지 포맷팅 유틸리티
// ============================================

import { getCurrentChatData, getCurrentProfile } from '../../app/state.js';
import { parseAIContentStreaming, getCharacterAvatar, renderAvatarHTML } from '../parser.js';
import { escapeHtml } from '../../utils.js';

// ============================================
// @silently 블록 완료 확인
// ============================================

export function isSilentlyComplete(text) {
  // @silently가 없으면 완료된 것으로 간주
  if (!text.includes('@silently')) {
    return true;
  }

  // @silently {{ ... }} 형태 (이중 중괄호)
  const doubleMatch = text.match(/@silently\s*\{\{/);
  if (doubleMatch) {
    // }} 이후에 실제 콘텐츠가 있는지 확인
    const afterDouble = text.split(/\}\}/);
    return afterDouble.length > 1 && afterDouble[afterDouble.length - 1].trim().length > 0;
  }

  // @silently { ... } 형태 (단일 중괄호)
  const singleMatch = text.match(/@silently\s*\{(?!\{)/);
  if (singleMatch) {
    // 중괄호 열고 닫힘 개수로 완료 판단
    const afterSilently = text.slice(text.indexOf('@silently'));
    let braceCount = 0;
    let foundOpen = false;
    for (const char of afterSilently) {
      if (char === '{') { braceCount++; foundOpen = true; }
      if (char === '}') braceCount--;
      if (foundOpen && braceCount === 0) return true;
    }
    return false;
  }

  return true;
}

// ============================================
// AI 응답 전처리
// ============================================

export function preprocessAIContent(text, isStreaming = false) {
  // 메모리 마커 제거 (<<< 이후 모두 제거)
  if (text && text.includes('<<<')) {
    text = text.split('<<<')[0].trim();
  }

  // @silently 블록 제거 (AI가 사용하는 모든 변형 처리)
  // 1. @silently {{ ... }} - 원래 포맷
  text = text.replace(/@silently\s*\{\{[\s\S]*?\}\}/gi, '');
  // 2. @silently { ... } - 단일 중괄호
  text = text.replace(/@silently\s*\{(?!\{)[\s\S]*?\}/gi, '');
  // 3. @silently ... @end
  text = text.replace(/@silently[\s\S]*?@end\s*/gi, '');
  // 4. @silently ... @/silently
  text = text.replace(/@silently[\s\S]*?@\/silently/gi, '');
  // 5. 단독 @silently 키워드 (위에서 처리되지 않은 것)
  text = text.replace(/@silently\s*/gi, '');

  // 타임스탬프 제거 (메시지 맨 처음에 나오는 것만, 중간 타임스탬프는 유지)
  text = text.replace(/^\s*\[\d{4}년\s*\d{1,2}월\s*\d{1,2}일\s*\d{1,2}:\d{2}\]\s*/, '');

  // AI가 잘못 사용한 이중 중괄호 패턴 제거 ({{user}}, {{USER_PLACEHOLDER}} 제외)
  text = text.replace(/\{\{(?!user\}\}|USER_PLACEHOLDER\}\})[\s\S]*?\}\}/gi, '');

  // 스트리밍 중 닫히지 않은 이중 중괄호도 제거
  if (isStreaming) {
    text = text.replace(/\{\{(?!user\}\}|USER_PLACEHOLDER\}\})[^}]*$/gi, '');
  }

  return text.trim();
}

// ============================================
// 블록 내용 정규화
// ============================================

export function normalizeBlockContent(text) {
  if (!text) return text;
  // 3개 이상의 연속 줄바꿈만 2개로 정규화 (단락 구분은 유지)
  return text.replace(/\n{3,}/g, '\n\n');
}

// ============================================
// 대사 내용 포맷팅
// ============================================

export function formatDialogueText(text) {
  if (!text) return text;
  // 연속된 줄바꿈 정규화
  text = normalizeBlockContent(text);
  // 【섹션 타이틀】 → 특별한 섹션 헤더
  text = text.replace(/【([^】]+)】/g, '<div class="saju-section-header">$1</div>');
  // **볼드**를 강조로 표시 (먼저 처리해야 *와 충돌 방지)
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong class="bold-text">$1</strong>');
  // *행동지문*을 회색으로 표시
  text = text.replace(/\*([^*]+)\*/g, '<span class="action-text">$1</span>');
  return text;
}

// ============================================
// 스트리밍용 포맷 함수
// ============================================

export function formatAIMessageStreaming(content) {
  // 공용 전처리 (메모리 마커, 이중 중괄호 제거)
  content = preprocessAIContent(content, true);

  // {{user}} 마커 처리 - 프로필이 있으면 이름으로, 없으면 보라색 플레이스홀더
  const currentChatData = getCurrentChatData();
  const currentProfile = getCurrentProfile();
  const userName = currentChatData?.userProfile?.name || currentProfile?.name || null;
  if (userName) {
    content = content.replace(/\{\{user\}\}/gi, userName);
  } else {
    content = content.replace(/\{\{user\}\}/gi, '{{USER_PLACEHOLDER}}');
  }

  let escaped = escapeHtml(content);
  // 플레이스홀더를 보라색 스타일의 {{user}}로 변환
  escaped = escaped.replace(/\{\{USER_PLACEHOLDER\}\}/g, '<span class="user-var-placeholder">{{user}}</span>');
  const blocks = parseAIContentStreaming(escaped);

  return blocks.map(block => {
    let innerHTML;
    const normalizedContent = normalizeBlockContent(block.content);
    // 모든 텍스트 블록에 **볼드** + *행동지문* 처리 적용
    const formattedContent = formatDialogueText(normalizedContent);
    if (block.type === 'narration') {
      innerHTML = `<div class="narration-block">${formattedContent}</div>`;
    } else if (block.type === 'image') {
      const imageIndex = parseInt(block.content.trim(), 10);
      const imageUrl = !isNaN(imageIndex) ? (currentChatData?.introImages?.[imageIndex] || '') : '';
      if (imageUrl) {
        innerHTML = `<div class="intro-image-block-chat"><img src="${imageUrl}" alt="이미지" data-action="showImage" data-url="${imageUrl}" data-name="이미지" onerror="this.parentElement&&(this.parentElement.style.display='none')"></div>`;
      } else {
        innerHTML = '';
      }
    } else if (block.type === 'situational') {
      // 상황별 이미지 렌더링
      const imageIndex = parseInt(block.content.trim(), 10);
      const situationalImages = currentChatData?.situationalImages || [];
      const imageData = !isNaN(imageIndex) ? situationalImages[imageIndex] : null;
      if (imageData?.imageUrl) {
        const altText = escapeHtml(imageData.description || '상황 이미지');
        innerHTML = `<div class="situational-image-block-chat"><img src="${imageData.imageUrl}" alt="${altText}" data-action="showImage" data-url="${imageData.imageUrl}" data-name="${altText}" onerror="this.parentElement&&(this.parentElement.style.display='none')"></div>`;
      } else {
        innerHTML = '';
      }
    } else if (block.type === 'helper') {
      innerHTML = `<div class="helper-block"><div class="helper-avatar">🌟</div><div class="helper-content"><div class="helper-speaker">별님이</div><div class="helper-bubble"><div class="helper-text">${formattedContent}</div></div></div></div>`;
    } else if (block.type === 'thought') {
      const avatarData = block.isExtra ? { type: 'extra' } : getCharacterAvatar(block.speaker);
      const avatarHTML = renderAvatarHTML(avatarData);
      innerHTML = `<div class="thought-block"><div class="thought-avatar">${avatarHTML}</div><div class="thought-content"><div class="thought-speaker">${escapeHtml(block.speaker)}</div><div class="thought-bubble"><div class="thought-text">${formattedContent}</div></div></div></div>`;
    } else if (block.type === 'dialogue') {
      const avatarData = block.isExtra ? { type: 'extra' } : getCharacterAvatar(block.speaker);
      const avatarHTML = renderAvatarHTML(avatarData);
      innerHTML = `<div class="dialogue-block"><div class="dialogue-avatar">${avatarHTML}</div><div class="dialogue-content"><div class="dialogue-speaker">${escapeHtml(block.speaker)}</div><div class="dialogue-bubble"><div class="dialogue-text">${formattedContent}</div></div></div></div>`;
    } else if (block.type === 'user-quote') {
      return `<div class="ai-content-block user-quote-wrapper"><div class="user-quote-block"><div class="user-quote-content">${formattedContent}</div></div></div>`;
    } else {
      innerHTML = `<div class="plain-text-block">${formattedContent}</div>`;
    }
    return `<div class="ai-content-block">${innerHTML}</div>`;
  }).join('');
}

